import React, { useState, useEffect } from 'react';
import { User, WorkLocation } from '../types';
import { formatTurkeyDate } from '../utils';
import { getAllWorkLocations, assignWorkLocationToUser, toggleUserDisabledStatus, updateUserPTO, updateUserDateOfBirth, updateUserDateOfHire } from '../firebase/firestore';

/**
 * AdminUsersList Component
 * 
 * Displays a list of all users with the ability to:
 * - Assign work locations to users
 * - Disable/Enable users with confirmation dialog
 * 
 * When a user is disabled:
 * - Their disabled flag is set to true in Firestore
 * - They are prevented from signing in (checked in auth.ts)
 * - Their appearance in the list is dimmed
 * - Location assignment is disabled for disabled users
 * 
 * Note: Actual Firebase Authentication disabling requires Firebase Admin SDK
 * which should be implemented in a backend service for production use.
 */

interface AdminUsersListProps {
  users: User[];
  onUserUpdate?: () => void;
}

const AdminUsersList: React.FC<AdminUsersListProps> = ({ users, onUserUpdate }) => {
  const [workLocations, setWorkLocations] = useState<WorkLocation[]>([]);
  const [assigningLocation, setAssigningLocation] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [disablingUser, setDisablingUser] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [userToDisable, setUserToDisable] = useState<User | null>(null);
  const [editingPTO, setEditingPTO] = useState<string | null>(null);
  const [ptoValue, setPtoValue] = useState<string>('');
  const [updatingPTO, setUpdatingPTO] = useState<string | null>(null);
  const [editingBirthDate, setEditingBirthDate] = useState<string | null>(null);
  const [editingHireDate, setEditingHireDate] = useState<string | null>(null);
  const [birthDateValue, setBirthDateValue] = useState<string>('');
  const [hireDateValue, setHireDateValue] = useState<string>('');
  const [updatingBirthDate, setUpdatingBirthDate] = useState<string | null>(null);
  const [updatingHireDate, setUpdatingHireDate] = useState<string | null>(null);

  useEffect(() => {
    loadWorkLocations();
  }, []);

  const loadWorkLocations = async () => {
    try {
      const locations = await getAllWorkLocations();
      setWorkLocations(locations);
    } catch (error) {
      console.error('Error loading work locations:', error);
    }
  };

  const handleAssignLocation = async (userId: string) => {
    if (!selectedLocationId) {
      alert('Lütfen bir konum seçin');
      return;
    }

    setLoading(true);
    try {
      await assignWorkLocationToUser(userId, selectedLocationId);
      setAssigningLocation(null);
      setSelectedLocationId('');
      // Refresh the page to show updated data
      window.location.reload();
    } catch (error) {
      console.error('Error assigning work location:', error);
      alert('Konum atanırken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableUserClick = (user: User) => {
    setUserToDisable(user);
    setShowConfirmDialog(true);
  };

  const handleConfirmDisable = async () => {
    if (!userToDisable) return;

    setDisablingUser(userToDisable.id);
    setShowConfirmDialog(false);
    
    try {
      const newDisabledStatus = !userToDisable.disabled;
      await toggleUserDisabledStatus(userToDisable.id, newDisabledStatus);
      
      // Call the callback to refresh user data
      if (onUserUpdate) {
        onUserUpdate();
      } else {
        // Fallback to page reload if no callback provided
        window.location.reload();
      }
    } catch (error) {
      console.error('Error toggling user disabled status:', error);
      alert('Kullanıcı durumu güncellenirken hata oluştu');
    } finally {
      setDisablingUser(null);
      setUserToDisable(null);
    }
  };

  const handleCancelDisable = () => {
    setShowConfirmDialog(false);
    setUserToDisable(null);
  };

  const handlePTOEdit = (user: User) => {
    setEditingPTO(user.id);
    setPtoValue(user.pto?.toString() || '0');
  };

  const handlePTOSave = async (userId: string) => {
         const ptoNumber = parseInt(ptoValue);
    if (isNaN(ptoNumber)) {
      alert('Lütfen geçerli bir yıllık izin değeri girin');
      return;
    }

    setUpdatingPTO(userId);
    try {
      await updateUserPTO(userId, ptoNumber);
      setEditingPTO(null);
      setPtoValue('');
      // Refresh the page to show updated data
      if (onUserUpdate) {
        onUserUpdate();
      } else {
        window.location.reload();
      }
         } catch (error) {
       console.error('Error updating user PTO:', error);
       alert('Yıllık izin güncellenirken hata oluştu');
     } finally {
      setUpdatingPTO(null);
    }
  };

  const handlePTOCancel = () => {
    setEditingPTO(null);
    setPtoValue('');
  };

  // Date editing functions
  const handleBirthDateEdit = (user: User) => {
    setEditingBirthDate(user.id);
    if (user.dateOfBirth) {
      // Convert to local date string to avoid timezone issues
      const localDate = new Date(user.dateOfBirth.getTime() - user.dateOfBirth.getTimezoneOffset() * 60000);
      setBirthDateValue(localDate.toISOString().split('T')[0]);
    } else {
      setBirthDateValue('');
    }
  };

  const handleHireDateEdit = (user: User) => {
    setEditingHireDate(user.id);
    if (user.dateOfHire) {
      // Convert to local date string to avoid timezone issues
      const localDate = new Date(user.dateOfHire.getTime() - user.dateOfHire.getTimezoneOffset() * 60000);
      setHireDateValue(localDate.toISOString().split('T')[0]);
    } else {
      setHireDateValue('');
    }
  };

  const handleBirthDateSave = async (userId: string) => {
    setUpdatingBirthDate(userId);
    try {
      await updateUserDateOfBirth(userId, birthDateValue);
      setEditingBirthDate(null);
      setBirthDateValue('');
      if (onUserUpdate) {
        onUserUpdate();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error updating birth date:', error);
      alert('Doğum tarihi güncellenirken hata oluştu');
    } finally {
      setUpdatingBirthDate(null);
    }
  };

  const handleHireDateSave = async (userId: string) => {
    setUpdatingHireDate(userId);
    try {
      await updateUserDateOfHire(userId, hireDateValue);
      setEditingHireDate(null);
      setHireDateValue('');
      if (onUserUpdate) {
        onUserUpdate();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error updating hire date:', error);
      alert('İşe giriş tarihi güncellenirken hata oluştu');
    } finally {
      setUpdatingHireDate(null);
    }
  };

  const handleBirthDateCancel = () => {
    setEditingBirthDate(null);
    setBirthDateValue('');
  };

  const handleHireDateCancel = () => {
    setEditingHireDate(null);
    setHireDateValue('');
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-2">Tüm Kullanıcılar</h2>
        <p className="text-sm text-gray-600">Sistemde kayıtlı tüm kullanıcıların listesi</p>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && userToDisable && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="ml-2 text-lg font-medium text-gray-900">
                Kullanıcıyı {userToDisable.disabled ? 'Aktifleştir' : 'Devre Dışı Bırak'}
              </h3>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-500">
                <strong>{userToDisable.name}</strong> ({userToDisable.email}) kullanıcısını {userToDisable.disabled ? 'aktifleştirmek' : 'devre dışı bırakmak'} istediğinizden emin misiniz?
              </p>
              {!userToDisable.disabled && (
                <p className="text-sm text-red-600 mt-2">
                  ⚠️ Devre dışı bırakılan kullanıcı sisteme giriş yapamayacak.
                </p>
              )}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelDisable}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={handleConfirmDisable}
                className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
                  userToDisable.disabled
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {userToDisable.disabled ? 'Aktifleştir' : 'Devre Dışı Bırak'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {users.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Henüz kullanıcı yok</h3>
            <p className="text-sm text-gray-500">Kullanıcılar sisteme giriş yaptığında burada görünecek</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                                     <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                     Kullanıcı
                   </th>
                   <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                     E-posta
                   </th>
                   <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                     İş Yeri Konumu
                   </th>
                   <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                     Kayıt
                   </th>
                   <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                     Durum
                   </th>
                                       <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                      Yıllık İzin Hakkı
                    </th>
                   <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                     Doğum Tarihi
                   </th>
                   <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                     İşe Giriş Tarihi
                   </th>
                   <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                     İşlemler
                   </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className={`hover:bg-gray-50 ${user.disabled ? 'opacity-60' : ''}`}>
                                         <td className="px-3 py-3 w-32">
                       <div className="flex items-center">
                         <div className="flex-shrink-0 h-6 w-6">
                           <div className={`h-6 w-6 rounded-full ${user.disabled ? 'bg-gray-400' : 'bg-gray-300'} flex items-center justify-center`}>
                             <span className="text-xs font-medium text-gray-700">
                               {user.name.charAt(0).toUpperCase()}
                             </span>
                           </div>
                         </div>
                         <div className="ml-2 min-w-0 flex-1">
                                                       <div className={`text-sm font-medium truncate ${user.disabled ? 'text-gray-500' : 'text-gray-900'}`}>
                              {user.name}
                              {user.disabled && <span className="ml-1 text-xs text-red-600">(D)</span>}
                            </div>
                            <div className="text-xs text-gray-500 truncate">ID: {user.id.substring(0, 6)}...</div>
                         </div>
                       </div>
                     </td>
                                           <td className="px-3 py-3 w-40">
                        <div className={`text-sm truncate ${user.disabled ? 'text-gray-500' : 'text-gray-900'}`} title={user.email}>{user.email}</div>
                      </td>
                                           <td className="px-3 py-3 w-48">
                        <div className="text-sm text-gray-900">
                          {user.workLocation.latitude === 0 && user.workLocation.longitude === 0 ? (
                            <span className="text-red-600">Konum yok</span>
                          ) : (
                            <div>
                              <div className="text-sm text-gray-500 truncate" title={`${user.workLocation.latitude.toFixed(6)}, ${user.workLocation.longitude.toFixed(6)}`}>
                                {user.workLocation.latitude.toFixed(4)}, {user.workLocation.longitude.toFixed(4)}
                              </div>
                              {user.workLocation.address && (
                                <div className="text-xs text-gray-600 truncate" title={user.workLocation.address}>
                                  {user.workLocation.address}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                                           <td className="px-3 py-3 w-24">
                        <div className="text-sm text-gray-500" title={formatTurkeyDate(user.createdAt)}>
                          {new Intl.DateTimeFormat('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          }).format(user.createdAt)}
                        </div>
                      </td>
                     <td className="px-3 py-3 w-20">
                       {user.disabled ? (
                         <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                           Devre Dışı
                         </span>
                       ) : user.workLocation.latitude === 0 && user.workLocation.longitude === 0 ? (
                         <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                           Bekliyor
                         </span>
                       ) : (
                         <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                           Aktif
                         </span>
                       )}
                     </td>
                                           <td className="px-3 py-3 w-16">
                        {editingPTO === user.id ? (
                          <div className="space-y-1">
                            <input
                              type="number"
                              value={ptoValue}
                              onChange={(e) => setPtoValue(e.target.value)}
                              className="w-full px-1 py-0.5 border border-gray-300 rounded text-xs"
                            />
                            <div className="flex space-x-1">
                              <button
                                onClick={() => handlePTOSave(user.id)}
                                disabled={updatingPTO === user.id}
                                className="bg-green-600 hover:bg-green-700 text-white px-2 py-0.5 rounded text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {updatingPTO === user.id ? '...' : '✓'}
                              </button>
                              <button
                                onClick={handlePTOCancel}
                                className="px-2 py-0.5 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-900">
                            {user.pto || 0}
                          </div>
                        )}
                      </td>
                     <td className="px-3 py-3 w-24">
                        {editingBirthDate === user.id ? (
                          <div className="space-y-1">
                            <input
                              type="date"
                              value={birthDateValue}
                              onChange={(e) => setBirthDateValue(e.target.value)}
                              className="w-full px-1 py-0.5 border border-gray-300 rounded text-xs"
                            />
                            <div className="flex space-x-1">
                              <button
                                onClick={() => handleBirthDateSave(user.id)}
                                disabled={updatingBirthDate === user.id}
                                className="bg-green-600 hover:bg-green-700 text-white px-2 py-0.5 rounded text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {updatingBirthDate === user.id ? '...' : '✓'}
                              </button>
                              <button
                                onClick={handleBirthDateCancel}
                                className="px-2 py-0.5 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-900">
                            {user.dateOfBirth ? (
                              <div>
                                <div className="text-xs text-gray-500">
                                  {new Intl.DateTimeFormat('tr-TR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                  }).format(user.dateOfBirth)}
                                </div>
                                <button
                                  onClick={() => handleBirthDateEdit(user)}
                                  disabled={user.disabled}
                                  className="text-blue-600 hover:text-blue-800 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Düzenle
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleBirthDateEdit(user)}
                                disabled={user.disabled}
                                className="text-blue-600 hover:text-blue-800 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Ekle
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                     <td className="px-3 py-3 w-24">
                        {editingHireDate === user.id ? (
                          <div className="space-y-1">
                            <input
                              type="date"
                              value={hireDateValue}
                              onChange={(e) => setHireDateValue(e.target.value)}
                              className="w-full px-1 py-0.5 border border-gray-300 rounded text-xs"
                            />
                            <div className="flex space-x-1">
                              <button
                                onClick={() => handleHireDateSave(user.id)}
                                disabled={updatingHireDate === user.id}
                                className="bg-green-600 hover:bg-green-700 text-white px-2 py-0.5 rounded text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {updatingHireDate === user.id ? '...' : '✓'}
                              </button>
                              <button
                                onClick={handleHireDateCancel}
                                className="px-2 py-0.5 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-900">
                            {user.dateOfHire ? (
                              <div>
                                <div className="text-xs text-gray-500">
                                  {new Intl.DateTimeFormat('tr-TR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                  }).format(user.dateOfHire)}
                                </div>
                                <button
                                  onClick={() => handleHireDateEdit(user)}
                                  disabled={user.disabled}
                                  className="text-blue-600 hover:text-blue-800 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Düzenle
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleHireDateEdit(user)}
                                disabled={user.disabled}
                                className="text-blue-600 hover:text-blue-800 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Ekle
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                     <td className="px-3 py-3 w-48 text-sm font-medium">
                                             <div className="flex flex-col space-y-2">
                        {assigningLocation === user.id ? (
                                                     <div className="space-y-1">
                             <select
                               value={selectedLocationId}
                               onChange={(e) => setSelectedLocationId(e.target.value)}
                               className="w-full px-1 py-0.5 border border-gray-300 rounded text-xs"
                             >
                               <option value="">Konum seçin...</option>
                               {workLocations.map((location) => (
                                 <option key={location.id} value={location.id}>
                                   {location.name} - {location.address}
                                 </option>
                               ))}
                             </select>
                             <div className="flex space-x-1">
                               <button
                                 onClick={() => handleAssignLocation(user.id)}
                                 disabled={loading || !selectedLocationId}
                                 className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-0.5 rounded text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                               >
                                 {loading ? '⏳' : '✓'}
                               </button>
                               <button
                                 onClick={() => {
                                   setAssigningLocation(null);
                                   setSelectedLocationId('');
                                 }}
                                 className="px-2 py-0.5 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50"
                               >
                                 ✕
                               </button>
                             </div>
                           </div>
                        ) : (
                          <>
                                                         <button
                               onClick={() => setAssigningLocation(user.id)}
                               className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed w-full"
                               disabled={user.disabled}
                             >
                               Konum Ata
                             </button>
                                                           <button
                                onClick={() => handlePTOEdit(user)}
                                className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed w-full"
                                disabled={user.disabled}
                              >
                                Yıllık İzin Düzenle
                              </button>
                                                           {user.email !== 'muhasebe@meconyapi.com' ? (
                                <button
                                  onClick={() => handleDisableUserClick(user)}
                                  disabled={disablingUser === user.id}
                                  className={`px-3 py-1 rounded text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed w-full ${
                                    user.disabled
                                      ? 'bg-green-600 hover:bg-green-700 text-white'
                                      : 'bg-red-600 hover:bg-red-700 text-white'
                                  }`}
                                >
                                  {disablingUser === user.id
                                    ? 'İşleniyor...'
                                    : user.disabled
                                    ? 'Aktifleştir'
                                    : 'Devre Dışı Bırak'}
                                </button>
                              ) : (
                                <span className="text-xs text-gray-400 px-3 py-1 w-full text-center" title="Korumalı Hesap">🔒 Korumalı</span>
                              )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersList; 